import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Drawer, EmptyState, Modal, Select, Toggle } from '@/components/ui'
import {
  useAssignExportLaneCustomer,
  useCreateExportLane,
  useExportLanes,
  useUnassignExportLaneCustomer,
  useUpdateExportLane,
} from '../api/queries'
import type { ExportDocType, ExportLane, ExportLaneCustomer, ExportLaneInput } from '../api/types'

const INCOTERM_OPTIONS = ['EXW', 'FOB', 'CIF', 'CFR', 'DDP', 'DAP', 'FCA']
const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'en', label: 'English (en)' },
  { value: 'es', label: 'Spanish (es)' },
]

// Mirrors `middleware/app/models/export_docs_packet.py`'s ALL_DOC_TYPES.
// Kept local rather than importing from `@/modules/export-docs` — cross-
// module imports are banned in this codebase (eslint no-restricted-imports).
const ALL_DOC_TYPES: readonly ExportDocType[] = [
  'proforma_invoice',
  'commercial_invoice',
  'packing_list',
  'sli',
  'cofa',
  'sds',
  'certificate_of_origin',
  'dg_declaration',
  'carton_labels',
  'bol_instructions',
] as const

const DOC_TYPE_LABELS: Record<ExportDocType, string> = {
  proforma_invoice: 'Proforma Invoice',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  sli: 'Shipping Letter of Instruction',
  cofa: 'Certificate of Analysis',
  sds: 'Safety Data Sheet',
  certificate_of_origin: 'Certificate of Origin',
  dg_declaration: 'Dangerous Goods Declaration',
  carton_labels: 'Carton Labels',
  bol_instructions: 'BL / AWB Instructions',
}

function emptyLaneInput(): ExportLaneInput {
  return {
    lane_code: '',
    lane_name: '',
    intermediary_legal_name: '',
    intermediary_address: '',
    intermediary_cnpj: '',
    intermediary_odoo_partner_id: undefined,
    forwarder_legal_name: '',
    forwarder_address: '',
    forwarder_contact_name: '',
    forwarder_contact_email: '',
    forwarder_contact_phone: '',
    default_incoterm: '',
    label_language: 'pt',
    required_doc_types: [],
    conditional_doc_rules: {},
    notes: '',
    active: true,
  }
}

/**
 * `GET /api/export-docs/lanes` now returns the full lane record
 * (address/CNPJ/forwarder contact/notes were added to that endpoint's
 * serialization alongside this UI — see `list_lanes` in
 * `middleware/app/api/routes/export_docs.py`). PATCH is still a full
 * overwrite (`for key, value in body.model_dump().items(): setattr(lane,
 * key, value)`), so the edit form must seed every field from the real
 * row rather than leaving any of them blank, or a save would silently
 * wipe data that just isn't shown in this form.
 */
function laneInputFromLane(lane: ExportLane): ExportLaneInput {
  return {
    lane_code: lane.lane_code,
    lane_name: lane.lane_name,
    intermediary_legal_name: lane.intermediary_legal_name,
    intermediary_address: lane.intermediary_address,
    intermediary_cnpj: lane.intermediary_cnpj ?? '',
    intermediary_odoo_partner_id: lane.intermediary_odoo_partner_id ?? undefined,
    forwarder_legal_name: lane.forwarder_legal_name,
    forwarder_address: lane.forwarder_address ?? '',
    forwarder_contact_name: lane.forwarder_contact_name ?? '',
    forwarder_contact_email: lane.forwarder_contact_email ?? '',
    forwarder_contact_phone: lane.forwarder_contact_phone ?? '',
    default_incoterm: lane.default_incoterm ?? '',
    label_language: lane.label_language,
    required_doc_types: lane.required_doc_types,
    conditional_doc_rules: lane.conditional_doc_rules,
    notes: lane.notes ?? '',
    active: lane.active,
  }
}

export function ExportLaneConfigSection() {
  const { data, isLoading } = useExportLanes()
  const createLane = useCreateExportLane()
  const updateLane = useUpdateExportLane()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingLane, setEditingLane] = useState<ExportLane | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const lanes = data?.lanes ?? []

  function toggleExpanded(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function openCreate() {
    setEditingLane(null)
    setDrawerOpen(true)
  }

  function openEdit(lane: ExportLane) {
    setEditingLane(lane)
    setDrawerOpen(true)
  }

  function handleSubmit(body: ExportLaneInput) {
    if (editingLane) {
      updateLane.mutate(
        { laneId: editingLane.id, body },
        {
          onSuccess: () => {
            toast.success(`Saved ${body.lane_name}`)
            setDrawerOpen(false)
          },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'save failed', { duration: 8000 }),
        },
      )
    } else {
      createLane.mutate(body, {
        onSuccess: () => {
          toast.success(`Created ${body.lane_name}`)
          setDrawerOpen(false)
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'create failed', { duration: 8000 }),
      })
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Export Lane Config</h2>
          <p className="text-sm text-secondary">
            Trading-intermediary + freight-forwarder setups for outbound export shipments (e.g. Brazil via
            Sertrading/Vanguard). Assign SPI customers to a lane so their outbound pickings generate an export
            documentation packet automatically.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={12} /> New lane
        </Button>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : lanes.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No export lanes configured"
          description="Create a lane to start routing customers through an export documentation pipeline."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {lanes.map((lane) => (
            <LaneRow
              key={lane.id}
              lane={lane}
              expanded={expanded.has(lane.id)}
              onToggleExpanded={() => toggleExpanded(lane.id)}
              onEdit={() => openEdit(lane)}
            />
          ))}
        </div>
      )}

      <LaneFormDrawer
        open={drawerOpen}
        lane={editingLane}
        submitting={createLane.isPending || updateLane.isPending}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
      />
    </section>
  )
}

function LaneRow({
  lane,
  expanded,
  onToggleExpanded,
  onEdit,
}: {
  lane: ExportLane
  expanded: boolean
  onToggleExpanded: () => void
  onEdit: () => void
}) {
  return (
    <div className="overflow-hidden rounded-sm border border-border-subtle bg-surface">
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${lane.lane_name}` : `Expand ${lane.lane_name}`}
          className="inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-tertiary hover:text-primary"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-primary">{lane.lane_name}</span>
            {!lane.active ? (
              <Chip size="xs" variant="neutral">
                Inactive
              </Chip>
            ) : null}
          </div>
          <div className="text-role-metadata text-tertiary">
            Intermediary: {lane.intermediary_legal_name} · Forwarder: {lane.forwarder_legal_name}
          </div>
        </div>
        {lane.default_incoterm ? (
          <Chip size="xs" variant="info">
            {lane.default_incoterm}
          </Chip>
        ) : null}
        <span className="text-role-metadata text-tertiary">
          {lane.customers.length} customer{lane.customers.length === 1 ? '' : 's'}
        </span>
        <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${lane.lane_name}`} onClick={onEdit}>
          <Pencil size={14} />
        </Button>
      </div>
      {expanded ? <LaneCustomersPanel lane={lane} /> : null}
    </div>
  )
}

function LaneCustomersPanel({ lane }: { lane: ExportLane }) {
  const assign = useAssignExportLaneCustomer()
  const unassign = useUnassignExportLaneCustomer()
  const [partnerId, setPartnerId] = useState('')
  const [nameCache, setNameCache] = useState('')
  const [pendingRemove, setPendingRemove] = useState<ExportLaneCustomer | null>(null)

  function handleAdd() {
    const id = Number(partnerId.trim())
    if (!partnerId.trim() || !Number.isInteger(id) || id <= 0) {
      toast.error('Enter a valid Odoo partner id (positive integer)')
      return
    }
    assign.mutate(
      { laneId: lane.id, odoo_partner_id: id, partner_name_cache: nameCache.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(`Assigned partner ${id} to ${lane.lane_name}`)
          setPartnerId('')
          setNameCache('')
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'assign failed'),
      },
    )
  }

  function confirmRemove() {
    if (!pendingRemove) return
    unassign.mutate(
      { laneId: lane.id, assignmentId: pendingRemove.id },
      {
        onSuccess: () => {
          toast.success(`Removed ${pendingRemove.partner_name_cache ?? pendingRemove.odoo_partner_id}`)
          setPendingRemove(null)
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'remove failed'),
      },
    )
  }

  return (
    <div className="border-t border-border-subtle bg-surface-sunk px-3 py-2">
      {lane.customers.length === 0 ? (
        <p className="m-0 py-1 text-sm text-tertiary">No customers assigned to this lane yet.</p>
      ) : (
        <ul className="mb-2 flex flex-col divide-y divide-border-subtle">
          {lane.customers.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-1.5 text-sm">
              <span className="flex-1 text-primary">
                {c.partner_name_cache ?? <em className="text-tertiary">(no cached name)</em>}
              </span>
              <span className="text-role-metadata text-tertiary">partner #{c.odoo_partner_id}</span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Remove partner ${c.odoo_partner_id}`}
                onClick={() => setPendingRemove(c)}
              >
                <Trash2 size={13} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-secondary">
          Odoo partner id
          <input
            type="text"
            inputMode="numeric"
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            placeholder="e.g. 4821"
            className="w-32 rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-secondary">
          Name (cache — for display only)
          <input
            type="text"
            value={nameCache}
            onChange={(e) => setNameCache(e.target.value)}
            placeholder="e.g. Weckerle Do Brasil Ltda"
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
        <Button variant="secondary" size="sm" loading={assign.isPending} onClick={handleAdd}>
          <Plus size={13} className="mr-1" />
          Assign
        </Button>
      </div>
      <p className="m-0 mt-1 text-xs text-tertiary">
        No partner-search picker is wired up here yet — enter the raw Odoo <code>res.partner</code> id. The name
        field is a display cache only; it isn&apos;t re-validated against Odoo.
      </p>

      {pendingRemove ? (
        <Modal
          open={pendingRemove !== null}
          onOpenChange={(open) => !open && setPendingRemove(null)}
          tone="danger"
          title="Remove customer from lane"
          description={
            <>
              Remove <code>{pendingRemove.partner_name_cache ?? `partner #${pendingRemove.odoo_partner_id}`}</code>{' '}
              from <code>{lane.lane_name}</code>? Their future outbound shipments will stop generating export
              packets on this lane until reassigned.
            </>
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingRemove(null)}>
                Cancel
              </Button>
              <Button variant="danger" loading={unassign.isPending} onClick={confirmRemove}>
                Remove
              </Button>
            </>
          }
        />
      ) : null}
    </div>
  )
}

function LaneFormDrawer({
  open,
  lane,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean
  lane: ExportLane | null
  submitting: boolean
  onClose: () => void
  onSubmit: (body: ExportLaneInput) => void
}) {
  const [form, setForm] = useState<ExportLaneInput>(() => (lane ? laneInputFromLane(lane) : emptyLaneInput()))
  const [seededKey, setSeededKey] = useState<number | 'new'>(lane?.id ?? 'new')

  // Re-seed the form whenever the drawer opens for a different lane
  // (including switching from edit to create). Comparing against a
  // remembered key avoids re-seeding on every parent re-render while
  // the drawer is open and the operator is mid-edit.
  const key = lane?.id ?? 'new'
  if (key !== seededKey) {
    setSeededKey(key)
    setForm(lane ? laneInputFromLane(lane) : emptyLaneInput())
  }

  const isEdit = lane !== null

  function update<K extends keyof ExportLaneInput>(field: K, value: ExportLaneInput[K]) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function toggleDocType(docType: ExportDocType) {
    setForm((prev) => ({
      ...prev,
      required_doc_types: prev.required_doc_types.includes(docType)
        ? prev.required_doc_types.filter((d) => d !== docType)
        : [...prev.required_doc_types, docType],
    }))
  }

  function handleSubmit() {
    if (!form.lane_code.trim() || !form.lane_name.trim()) {
      toast.error('Lane code and lane name are required')
      return
    }
    if (!form.intermediary_legal_name.trim() || !form.intermediary_address.trim()) {
      toast.error('Intermediary legal name and address are required')
      return
    }
    if (!form.forwarder_legal_name.trim()) {
      toast.error('Forwarder legal name is required')
      return
    }
    onSubmit({
      ...form,
      intermediary_cnpj: form.intermediary_cnpj?.trim() || null,
      forwarder_address: form.forwarder_address?.trim() || null,
      forwarder_contact_name: form.forwarder_contact_name?.trim() || null,
      forwarder_contact_email: form.forwarder_contact_email?.trim() || null,
      forwarder_contact_phone: form.forwarder_contact_phone?.trim() || null,
      default_incoterm: form.default_incoterm?.trim() || null,
      notes: form.notes?.trim() || null,
    })
  }

  const docTypeOptions = useMemo(() => ALL_DOC_TYPES, [])

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="wide"
      title={isEdit ? `Edit ${lane?.lane_name}` : 'New export lane'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            {isEdit ? 'Save lane' : 'Create lane'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 p-1">
        <FieldGroup title="Identity">
          <TextField label="Lane code" value={form.lane_code} onChange={(v) => update('lane_code', v)} placeholder="BR-SERTRADING" />
          <TextField label="Lane name" value={form.lane_name} onChange={(v) => update('lane_name', v)} placeholder="Brazil via Sertrading" />
          <label className="flex items-center gap-2 text-sm text-secondary">
            <Toggle checked={form.active} onChange={(e) => update('active', e.target.checked)} />
            Active
          </label>
        </FieldGroup>

        <FieldGroup title="Importer / Buyer / Consignee / Notify Party (intermediary)">
          <TextField
            label="Legal name"
            value={form.intermediary_legal_name}
            onChange={(v) => update('intermediary_legal_name', v)}
            placeholder="BTG Pactual Commodities Sertrading S.A."
          />
          <TextArea label="Address" value={form.intermediary_address} onChange={(v) => update('intermediary_address', v)} />
          <TextField label="CNPJ" value={form.intermediary_cnpj ?? ''} onChange={(v) => update('intermediary_cnpj', v)} />
          <TextField
            label="Odoo partner id (optional)"
            value={form.intermediary_odoo_partner_id != null ? String(form.intermediary_odoo_partner_id) : ''}
            onChange={(v) => update('intermediary_odoo_partner_id', v.trim() ? Number(v) || null : null)}
          />
        </FieldGroup>

        <FieldGroup title="Freight Forwarder">
          <TextField
            label="Legal name"
            value={form.forwarder_legal_name}
            onChange={(v) => update('forwarder_legal_name', v)}
            placeholder="Vanguard Logistics"
          />
          <TextArea label="Address" value={form.forwarder_address ?? ''} onChange={(v) => update('forwarder_address', v)} />
          <TextField label="Contact name" value={form.forwarder_contact_name ?? ''} onChange={(v) => update('forwarder_contact_name', v)} />
          <TextField label="Contact email" value={form.forwarder_contact_email ?? ''} onChange={(v) => update('forwarder_contact_email', v)} />
          <TextField label="Contact phone" value={form.forwarder_contact_phone ?? ''} onChange={(v) => update('forwarder_contact_phone', v)} />
        </FieldGroup>

        <FieldGroup title="Documents">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Default incoterm</span>
            <Select value={form.default_incoterm ?? ''} onChange={(e) => update('default_incoterm', e.target.value)}>
              <option value="">—</option>
              {INCOTERM_OPTIONS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Label language</span>
            <Select value={form.label_language} onChange={(e) => update('label_language', e.target.value)}>
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Required documents</span>
            <div className="flex flex-wrap gap-2">
              {docTypeOptions.map((dt) => (
                <label key={dt} className="flex items-center gap-1.5 text-xs text-secondary">
                  <input
                    type="checkbox"
                    checked={form.required_doc_types.includes(dt)}
                    onChange={() => toggleDocType(dt)}
                  />
                  {DOC_TYPE_LABELS[dt]}
                </label>
              ))}
            </div>
          </div>
          <TextArea label="Notes" value={form.notes ?? ''} onChange={(v) => update('notes', v)} />
        </FieldGroup>
      </div>
    </Drawer>
  )
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-2 rounded-sm border border-border-subtle p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-tertiary">{title}</legend>
      {children}
    </fieldset>
  )
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-secondary">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
      />
    </label>
  )
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-secondary">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
      />
    </label>
  )
}
